/**
 * AuthRoute — guard for client-side dashboard routes.
 *
 * Reads the session through `authSession`, which fetches `/api/auth/me` once
 * per document and shares it. That matters here because this guard remounts
 * on every guarded navigation: it used to refetch each time and render `null`
 * while waiting, so each route change blanked the screen for a round trip —
 * very visible on a slow connection.
 *
 * Now the first paint after that initial check is synchronous (peekSession),
 * so navigation no longer blanks. On 401 we hard-navigate to
 * `/login?redirect=<currentPath>` so the user lands back here after signing in.
 */
import React, { ReactNode, useEffect, useState } from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';

import { getSession, peekSession, Session } from '@/features/auth/session/authSession';

export interface AuthUser {
  id: string;
  email: string;
  name: string;
}

interface InnerProps extends RouteComponentProps {
  // `token` is the raw session JWT — passed so children can authenticate to
  // Bearer-only cross-origin Tile services (e.g. tile-push / the OTA console).
  // Most callers ignore it.
  children: (user: AuthUser, token: string) => ReactNode;
}

function AuthRouteInner({ children, location }: InnerProps) {
  // Seed from the cache so an already-known session renders on the first
  // frame instead of after a round trip.
  const known = peekSession();
  const [session, setSession] = useState<Session | null | undefined>(known);

  useEffect(() => {
    let cancelled = false;
    if (known !== undefined) {
      // Already resolved this document — nothing to fetch.
      if (known === null) {
        const target = encodeURIComponent(`${location.pathname}${location.search}`);
        window.location.replace(`/login?redirect=${target}`);
      }
      return undefined;
    }
    getSession().then((s) => {
      if (cancelled) return;
      if (s) {
        setSession(s);
        return;
      }
      const target = encodeURIComponent(`${location.pathname}${location.search}`);
      window.location.replace(`/login?redirect=${target}`);
    });
    return () => {
      cancelled = true;
    };
  }, [known, location.pathname, location.search]);

  if (!session) return null;
  return <>{children(session.user, session.token)}</>;
}

export default withRouter(AuthRouteInner);
